/**
 * API client functions for the Metis Web UI.
 * In dev, REST/auth/SSE all target the backend origin directly so cookies stay
 * on a single origin. In production, requests stay same-origin.
 */
import type { WorkflowArtifact, WorkflowRunStatus } from '@/lib/types';
import type { components } from '@/lib/api.generated';
import { buildBackendUrl } from '@/lib/auth-routing';

export type WorkflowDefinition = components['schemas']['WorkflowDefinition'];
export type DagNode = components['schemas']['DagNode'];
export type CurrentUser = components['schemas']['CurrentUser'];
export type AdminUser = components['schemas']['AdminUser'];
export type AuditLogEntry = components['schemas']['AuditLogEntry'];
export type GithubRepoPickerItem = components['schemas']['GithubRepoPickerItem'];
export type CodebaseResponse = components['schemas']['Codebase'];

// Artifact types (Slice 3 — derived from Slice 2's OpenAPI schemas)
export type Artifact = components['schemas']['Artifact'];
export type ArtifactType = components['schemas']['ArtifactType'];
export type ArtifactScope = components['schemas']['ArtifactScope'];
export type ArtifactStatus = components['schemas']['ArtifactStatus'];
export type ArtifactVisibility = components['schemas']['ArtifactVisibility'];
export type ArtifactListResponse = components['schemas']['ArtifactListResponse'];
export type ArtifactResponse = components['schemas']['ArtifactResponse'];
export type CreateArtifactBody = components['schemas']['CreateArtifactBody'];
// Slice 4: detail-view schemas
export type ArtifactSnapshot = components['schemas']['ArtifactSnapshot'];
export type ArtifactSnapshotListResponse = components['schemas']['ArtifactSnapshotListResponse'];
export type ArtifactActivity = components['schemas']['ArtifactActivity'];
export type ArtifactActivityListResponse = components['schemas']['ArtifactActivityListResponse'];
export type PatchArtifactBody = components['schemas']['PatchArtifactBody'];
export type UpdateArtifactBody = components['schemas']['UpdateArtifactBody'];
export type CreateArtifactSnapshotBody = components['schemas']['CreateArtifactSnapshotBody'];
export type RevokeArtifactSnapshotBody = components['schemas']['RevokeArtifactSnapshotBody'];
export type UnarchiveRestoreStatus = components['schemas']['UnarchiveRestoreStatus'];
// Slice 5: relationship schemas
export type ArtifactLink = components['schemas']['ArtifactLink'];
export type ArtifactLinkType = components['schemas']['ArtifactLinkType'];
export type ArtifactLinkListResponse = components['schemas']['ArtifactLinkListResponse'];
export type CreateArtifactLinkBody = components['schemas']['CreateArtifactLinkBody'];
export type ArtifactExternalRef = components['schemas']['ArtifactExternalRef'];
export type ArtifactExternalRefListResponse =
  components['schemas']['ArtifactExternalRefListResponse'];
export type ArtifactExternalProvider = components['schemas']['ArtifactExternalProvider'];
export type ArtifactExternalSyncDirection = components['schemas']['ArtifactExternalSyncDirection'];
export type CreateArtifactExternalRefBody = components['schemas']['CreateArtifactExternalRefBody'];
export type SupersedeAdrBody = components['schemas']['SupersedeAdrBody'];
// Slice 10: Work Areas and artifact applicability schemas
export type WorkArea = components['schemas']['WorkArea'];
export type WorkAreaVisibility = WorkArea['visibility'];
export type WorkAreaListResponse = components['schemas']['WorkAreaListResponse'];
export type WorkAreaResponse = components['schemas']['WorkAreaResponse'];
export type CreateWorkAreaBody = components['schemas']['CreateWorkAreaBody'];
export type PatchWorkAreaBody = components['schemas']['PatchWorkAreaBody'];
export type WorkAreaCodebase = components['schemas']['WorkAreaCodebase'];
export type WorkAreaCodebaseListResponse = components['schemas']['WorkAreaCodebaseListResponse'];
export type AddWorkAreaCodebaseBody = components['schemas']['AddWorkAreaCodebaseBody'];
export type WorkAreaEnvVar = components['schemas']['WorkAreaEnvVar'];
export type WorkAreaEnvVarListResponse = components['schemas']['WorkAreaEnvVarListResponse'];
export type SetWorkAreaEnvVarBody = components['schemas']['SetWorkAreaEnvVarBody'];
export type ArtifactApplicability = components['schemas']['ArtifactApplicability'];
export type ArtifactApplicabilityListResponse =
  components['schemas']['ArtifactApplicabilityListResponse'];
export type CreateArtifactApplicabilityBody =
  components['schemas']['CreateArtifactApplicabilityBody'];
// Slice 11: GitHub work-item schemas
export type GitHubWorkItemState = components['schemas']['GitHubWorkItemState'];
export type GitHubWorkItem = components['schemas']['GitHubWorkItem'];
export type GitHubWorkItemListResponse = components['schemas']['GitHubWorkItemListResponse'];
export type GitHubWorkItemDetailResponse = components['schemas']['GitHubWorkItemDetailResponse'];
export type ArtifactWorkItemLinkKind = components['schemas']['ArtifactWorkItemLinkKind'];
export type ArtifactWorkItemLink = components['schemas']['ArtifactWorkItemLink'];
export type ArtifactWorkItemLinkListResponse =
  components['schemas']['ArtifactWorkItemLinkListResponse'];
export type CreateArtifactWorkItemLinkBody =
  components['schemas']['CreateArtifactWorkItemLinkBody'];
export type SuccessResponse = components['schemas']['SuccessResponse'];
// Slice 12: Artifact GitHub publication schemas
export type ArtifactPublicationDriftState = components['schemas']['ArtifactPublicationDriftState'];
export type ArtifactPublication = components['schemas']['ArtifactPublication'];
export type ArtifactPublicationListResponse =
  components['schemas']['ArtifactPublicationListResponse'];
export type ArtifactPublicationResponse = components['schemas']['ArtifactPublicationResponse'];
export type PublishArtifactBody = components['schemas']['PublishArtifactBody'];
// Slice 13: artifact AI collaboration schemas
export type AiAskBody = components['schemas']['AiAskBody'];
export type AiGenerateScope = components['schemas']['AiGenerateScope'];
export type AiGenerateBody = components['schemas']['AiGenerateBody'];
export type AiSuggestionTargetIntent = components['schemas']['AiSuggestionTargetIntent'];
export type AiSuggestBody = components['schemas']['AiSuggestBody'];
export type AiRejectBody = components['schemas']['AiRejectBody'];
export type AiApplicationResponse = components['schemas']['AiApplicationResponse'];
export type AiSuggestion = components['schemas']['AiSuggestion'];
export type AiSuggestionListResponse = components['schemas']['AiSuggestionListResponse'];
export type AiMessage = components['schemas']['AiMessage'];
export type AiMessageResponse = components['schemas']['AiMessageResponse'];
// Slice 14: admin system status schemas
export type KillSwitchState = components['schemas']['KillSwitchState'];
export type ReconcileDiff = components['schemas']['ReconcileDiff'];
export type PublicationReconcileDecision = components['schemas']['PublicationReconcileDecision'];
export type PublicationReconcileDecisionResponse =
  components['schemas']['PublicationReconcileDecisionResponse'];
export type WorkAreaDriftState = components['schemas']['WorkAreaDriftState'];
export type WorkAreaRecentDriftArtifact = components['schemas']['WorkAreaRecentDriftArtifact'];
export type WorkAreaDriftSummary = components['schemas']['WorkAreaDriftSummary'];

export {
  ArtifactStorageDisabledError,
  listArtifacts,
  getArtifact,
  createArtifact,
  listSnapshots,
  listActivity,
  patchArtifact,
  deleteArtifact,
  createSnapshot,
  approveSnapshot,
  revokeSnapshot,
  listLinks,
  createLink,
  deleteLink,
  listExternalRefs,
  createExternalRef,
  deleteExternalRef,
  supersedeArtifact,
  flushArtifactCollaboration,
} from './artifacts-api';
export {
  listWorkAreas,
  createWorkArea,
  getWorkArea,
  patchWorkArea,
  deleteWorkArea,
  listWorkAreaCodebases,
  addWorkAreaCodebase,
  removeWorkAreaCodebase,
  listWorkAreaEnvVars,
  setWorkAreaEnvVar,
  deleteWorkAreaEnvVar,
  listApplicability,
  createApplicability,
  deleteApplicability,
} from './work-areas-api';
export {
  getWorkItem,
  reconcileWorkItem,
  listWorkItemsForWorkArea,
  listArtifactWorkItemLinks,
} from './github-work-items-api';
export { listPublications, checkDrift } from './artifact-publications-api';

function resolveApiUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  return buildBackendUrl(path);
}

export interface ConversationResponse {
  id: string;
  platform_type: string;
  platform_conversation_id: string;
  codebase_id: string | null;
  work_area_id: string | null;
  cwd: string | null;
  ai_assistant_type: string;
  title: string | null;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string;
  owner_id: string | null;
  visibility: 'team' | 'private';
  active_user: {
    id: string;
    display_name: string;
  } | null;
  active_expires_at: string | null;
}

export interface HealthResponse {
  status: string;
  adapter: string;
  concurrency: {
    active: number;
    queuedTotal: number;
    maxConcurrent: number;
  };
  runningWorkflows: number;
  version?: string;
  is_docker: boolean;
}

export interface ReadinessCheck {
  status: 'ok' | 'error';
  message: string;
  details?: Record<string, unknown>;
}

export interface ReadinessResponse {
  status: 'ready' | 'not_ready';
  checks: {
    databaseMigrations: ReadinessCheck;
    secretStore: ReadinessCheck;
    eventBus: ReadinessCheck;
    objectStore: ReadinessCheck;
    authConfig: ReadinessCheck;
    githubAppConfig: ReadinessCheck;
    workflowDispatch: ReadinessCheck;
    collab: ReadinessCheck;
    githubAppReachability: ReadinessCheck;
  };
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const requestUrl = resolveApiUrl(url);
  const res = await fetch(requestUrl, {
    ...options,
    credentials: options?.credentials ?? 'include',
  });
  if (!res.ok) {
    const body = await res.text();
    const truncated = body.length > 200 ? body.slice(0, 200) + '…' : body;
    const path = new URL(requestUrl, window.location.origin).pathname;
    const error = new Error(`API error ${String(res.status)} (${path}): ${truncated}`);
    (error as Error & { status: number }).status = res.status;
    throw error;
  }
  return res.json() as Promise<T>;
}

export async function getWorkAreaDriftSummary(workAreaId: string): Promise<WorkAreaDriftSummary> {
  return fetchJSON<WorkAreaDriftSummary>(
    `/api/work-areas/${encodeURIComponent(workAreaId)}/drift-summary`
  );
}

// Providers
export interface ProviderInfo {
  id: string;
  displayName: string;
  capabilities: Record<string, boolean>;
  builtIn: boolean;
}

export type ProviderDefaults = Record<string, unknown>;

export interface SafeConfigResponse {
  botName: string;
  assistant: string;
  assistants: Record<string, ProviderDefaults>;
  streaming: {
    slack: 'stream' | 'batch';
  };
  concurrency: {
    maxConversations: number;
  };
  defaults: {
    copyDefaults: boolean;
    loadDefaultCommands: boolean;
    loadDefaultWorkflows: boolean;
  };
}

export interface UpdateAssistantConfigBody {
  assistant?: string;
  assistants?: Record<string, ProviderDefaults>;
}

type AuthSessionResponse = components['schemas']['AuthSessionResponse'];
type GithubRepoPickerListResponse = components['schemas']['GithubRepoPickerListResponse'];

export async function listProviders(): Promise<ProviderInfo[]> {
  const data = await fetchJSON<{ providers: ProviderInfo[] }>('/api/providers');
  return data.providers;
}

export async function getAuthSession(): Promise<CurrentUser | null> {
  try {
    const data = await fetchJSON<AuthSessionResponse>('/api/auth/session');
    return data.user;
  } catch (error) {
    if ((error as Error & { status?: number }).status === 401) {
      return null;
    }

    throw error;
  }
}

export async function logoutAuthSession(): Promise<{ success: boolean }> {
  return fetchJSON('/api/auth/logout', { method: 'POST' });
}

export async function listAdminUsers(): Promise<AdminUser[]> {
  const data = await fetchJSON<components['schemas']['AdminUserListResponse']>('/api/admin/users');
  return data.users;
}

export async function updateAdminUserRole(
  userId: string,
  role: CurrentUser['role']
): Promise<{ success: boolean }> {
  return fetchJSON(`/api/admin/users/${encodeURIComponent(userId)}/role`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role }),
  });
}

export async function updateAdminUserActive(
  userId: string,
  active: boolean
): Promise<{ success: boolean }> {
  return fetchJSON(`/api/admin/users/${encodeURIComponent(userId)}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ active }),
  });
}

export async function transferOwnership(
  resourceType: 'codebase' | 'conversation' | 'workflow' | 'command',
  resourceId: string,
  newOwnerId: string
): Promise<{ success: boolean }> {
  return fetchJSON(
    `/api/admin/transfer/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newOwnerId }),
    }
  );
}

export async function listAuditLog(params?: {
  limit?: number;
  offset?: number;
  action?: string;
  resource_type?: string;
  actor_id?: string;
}): Promise<{ entries: AuditLogEntry[] }> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  if (params?.action) qs.set('action', params.action);
  if (params?.resource_type) qs.set('resource_type', params.resource_type);
  if (params?.actor_id) qs.set('actor_id', params.actor_id);
  const query = qs.toString();
  return fetchJSON(`/api/admin/audit-log${query ? `?${query}` : ''}`);
}

export async function updateConversationVisibility(
  id: string,
  visibility: 'team' | 'private'
): Promise<{ success: boolean }> {
  return fetchJSON(`/api/conversations/${encodeURIComponent(id)}/visibility`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ visibility }),
  });
}

// BYOK — Provider API key management
export type SecretProvider = components['schemas']['SecretProvider'];
export type ProviderKeyStatus = components['schemas']['ProviderKeyStatus'];
export type ProviderKeyListResponse = components['schemas']['ProviderKeyListResponse'];

export async function getPersonalProviderKeys(): Promise<ProviderKeyListResponse> {
  return fetchJSON<ProviderKeyListResponse>('/api/settings/provider-keys');
}

export async function setPersonalProviderKey(
  provider: SecretProvider,
  secret: string
): Promise<ProviderKeyStatus> {
  return fetchJSON<ProviderKeyStatus>(
    `/api/settings/provider-keys/${encodeURIComponent(provider)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret }),
    }
  );
}

export async function deletePersonalProviderKey(
  provider: SecretProvider
): Promise<{ success: boolean }> {
  return fetchJSON(`/api/settings/provider-keys/${encodeURIComponent(provider)}`, {
    method: 'DELETE',
  });
}

export async function getEnterpriseProviderKeys(): Promise<ProviderKeyListResponse> {
  return fetchJSON<ProviderKeyListResponse>('/api/admin/provider-keys');
}

export async function setEnterpriseProviderKey(
  provider: SecretProvider,
  secret: string
): Promise<ProviderKeyStatus> {
  return fetchJSON<ProviderKeyStatus>(`/api/admin/provider-keys/${encodeURIComponent(provider)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ secret }),
  });
}

export async function deleteEnterpriseProviderKey(
  provider: SecretProvider
): Promise<{ success: boolean }> {
  return fetchJSON(`/api/admin/provider-keys/${encodeURIComponent(provider)}`, {
    method: 'DELETE',
  });
}

// Provider account connections
export type ProviderConnectionProvider = components['schemas']['ProviderConnectionProvider'];
export type ProviderConnectionStatus = components['schemas']['ProviderConnectionStatus'];
export type ProviderConnectionAttempt = components['schemas']['ProviderConnectionAttempt'];
export type ProviderConnectionListResponse =
  components['schemas']['ProviderConnectionListResponse'];
export type ClaudeTokenRequest = components['schemas']['ClaudeTokenRequest'];

export async function getProviderConnections(): Promise<ProviderConnectionListResponse> {
  return fetchJSON<ProviderConnectionListResponse>('/api/settings/provider-connections');
}

export async function startProviderConnection(
  provider: ProviderConnectionProvider
): Promise<{ attempt: ProviderConnectionAttempt }> {
  return fetchJSON(`/api/settings/provider-connections/${encodeURIComponent(provider)}/start`, {
    method: 'POST',
  });
}

export async function getProviderConnectionAttempt(
  attemptId: string
): Promise<ProviderConnectionAttempt> {
  return fetchJSON(`/api/settings/provider-connections/attempts/${encodeURIComponent(attemptId)}`);
}

export async function disconnectProviderConnection(
  provider: ProviderConnectionProvider
): Promise<{ success: boolean }> {
  return fetchJSON(`/api/settings/provider-connections/${encodeURIComponent(provider)}`, {
    method: 'DELETE',
  });
}

export async function saveClaudeProviderToken(token: string): Promise<ProviderConnectionStatus> {
  const body: ClaudeTokenRequest = { token };
  return fetchJSON<ProviderConnectionStatus>('/api/settings/provider-connections/claude/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// Conversations
export async function listConversations(
  codebaseId?: string,
  workAreaId?: string
): Promise<ConversationResponse[]> {
  const params = new URLSearchParams();
  if (codebaseId) params.set('codebaseId', codebaseId);
  if (workAreaId) params.set('workAreaId', workAreaId);
  const qs = params.toString();
  return fetchJSON<ConversationResponse[]>(`/api/conversations${qs ? `?${qs}` : ''}`);
}

export async function createConversation(
  codebaseId?: string,
  message?: string,
  workAreaId?: string
): Promise<{ conversationId: string; id: string; dispatched?: boolean }> {
  const body: Record<string, string> = {};
  if (codebaseId) body.codebaseId = codebaseId;
  if (workAreaId) body.workAreaId = workAreaId;
  if (message) body.message = message;

  return fetchJSON('/api/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function getConversation(id: string): Promise<ConversationResponse> {
  return fetchJSON<ConversationResponse>(`/api/conversations/${encodeURIComponent(id)}`);
}

export async function updateConversation(
  id: string,
  updates: { title?: string; workAreaId?: string | null }
): Promise<{ success: boolean }> {
  return fetchJSON(`/api/conversations/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates),
  });
}

export async function deleteConversation(id: string): Promise<{ success: boolean }> {
  return fetchJSON(`/api/conversations/${id}`, { method: 'DELETE' });
}

export async function releaseConversationLock(id: string): Promise<void> {
  const url = resolveApiUrl(`/api/conversations/${encodeURIComponent(id)}/lock`);
  await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    keepalive: true,
  }).catch(() => undefined);
}

export async function sendMessage(
  conversationId: string,
  message: string,
  files?: File[]
): Promise<{ accepted: boolean; status: string }> {
  const url = `/api/conversations/${encodeURIComponent(conversationId)}/message`;

  if (!files || files.length === 0) {
    return fetchJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message }),
    });
  }

  const form = new FormData();
  form.append('message', message);
  for (const file of files) {
    form.append('files', file, file.name);
  }
  // No Content-Type header — browser sets multipart/form-data with boundary automatically
  return fetchJSON(url, { method: 'POST', body: form });
}

// Messages
export interface MessageResponse {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  metadata: string;
  created_at: string;
}

export async function getMessages(conversationId: string, limit = 200): Promise<MessageResponse[]> {
  return fetchJSON<MessageResponse[]>(
    `/api/conversations/${encodeURIComponent(conversationId)}/messages?limit=${String(limit)}`
  );
}

// Codebases
export async function listCodebases(): Promise<CodebaseResponse[]> {
  return fetchJSON<CodebaseResponse[]>('/api/codebases');
}

export async function listGithubRepos(): Promise<GithubRepoPickerItem[]> {
  return fetchJSON<GithubRepoPickerListResponse>('/api/github/repos');
}

export async function getCodebase(id: string): Promise<CodebaseResponse> {
  return fetchJSON<CodebaseResponse>(`/api/codebases/${id}`);
}

export async function addCodebase(input: { githubRepoId: string }): Promise<CodebaseResponse> {
  return fetchJSON<CodebaseResponse>('/api/codebases', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function refreshCodebaseGithubAccess(id: string): Promise<CodebaseResponse> {
  return fetchJSON<CodebaseResponse>(
    `/api/codebases/${encodeURIComponent(id)}/refresh-github-access`,
    {
      method: 'POST',
    }
  );
}

export async function deleteCodebase(id: string): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/codebases/${id}`, { method: 'DELETE' });
}

export interface WorkflowRunResponse {
  id: string;
  workflow_name: string;
  conversation_id: string | null;
  parent_conversation_id: string | null;
  codebase_id: string | null;
  work_area_id: string | null;
  status: WorkflowRunStatus;
  user_message: string;
  metadata: Record<string, unknown>;
  started_at: string;
  completed_at: string | null;
  last_activity_at: string | null;
  worker_platform_id?: string;
  parent_platform_id?: string;
  conversation_platform_id?: string;
}

export interface WorkflowEventResponse {
  id: string;
  workflow_run_id: string;
  event_type: string;
  step_index: number | null;
  step_name: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

export interface WorkflowArtifactListResponse {
  artifacts: WorkflowArtifact[];
}

export type WorkflowListEntry = components['schemas']['WorkflowListEntry'];

export async function listWorkflows(cwd?: string): Promise<WorkflowListEntry[]> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  const result = await fetchJSON<{ workflows: WorkflowListEntry[] }>(`/api/workflows${params}`);
  return result.workflows;
}

export async function runWorkflow(
  name: string,
  conversationId: string,
  message: string
): Promise<{ accepted: boolean; status: string }> {
  return fetchJSON(`/api/workflows/${encodeURIComponent(name)}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationId, message }),
  });
}

// Dashboard-enriched workflow run (includes joined data from single SQL query)
export interface DashboardRunResponse extends Omit<
  WorkflowRunResponse,
  'worker_platform_id' | 'parent_platform_id'
> {
  working_path: string | null;
  codebase_name: string | null;
  platform_type: string | null;
  worker_platform_id: string | null;
  parent_platform_id: string | null;
  current_step_name: string | null;
  total_steps: number | null;
  current_step_status: 'running' | 'completed' | 'failed' | null;
  agents_completed: number | null;
  agents_failed: number | null;
  agents_total: number | null;
}

/** Status counts across all matching runs (ignoring status filter). */
export interface DashboardCounts {
  all: number;
  running: number;
  completed: number;
  failed: number;
  cancelled: number;
  pending: number;
  paused: number;
}

/** Paginated dashboard runs response. */
export interface DashboardRunsResult {
  runs: DashboardRunResponse[];
  total: number;
  counts: DashboardCounts;
}

export async function listDashboardRuns(options?: {
  status?: WorkflowRunStatus;
  codebaseId?: string;
  search?: string;
  after?: string;
  before?: string;
  limit?: number;
  offset?: number;
}): Promise<DashboardRunsResult> {
  const params = new URLSearchParams();
  if (options?.status) params.set('status', options.status);
  if (options?.codebaseId) params.set('codebaseId', options.codebaseId);
  if (options?.search) params.set('search', options.search);
  if (options?.after) params.set('after', options.after);
  if (options?.before) params.set('before', options.before);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return fetchJSON<DashboardRunsResult>(`/api/dashboard/runs${qs ? `?${qs}` : ''}`);
}

export async function cancelWorkflowRun(
  runId: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
  });
}

export async function resumeWorkflowRun(
  runId: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/resume`, {
    method: 'POST',
  });
}

export async function abandonWorkflowRun(
  runId: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/abandon`, {
    method: 'POST',
  });
}

export async function deleteWorkflowRun(
  runId: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}`, {
    method: 'DELETE',
  });
}

export async function approveWorkflowRun(
  runId: string,
  comment?: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comment }),
  });
}

export async function rejectWorkflowRun(
  runId: string,
  reason?: string
): Promise<{ success: boolean; message: string }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export async function listWorkflowRuns(options?: {
  conversationId?: string;
  status?: WorkflowRunStatus;
  limit?: number;
  codebaseId?: string;
}): Promise<WorkflowRunResponse[]> {
  const params = new URLSearchParams();
  if (options?.conversationId) params.set('conversationId', options.conversationId);
  if (options?.status) params.set('status', options.status);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.codebaseId) params.set('codebaseId', options.codebaseId);
  const qs = params.toString();
  const result = await fetchJSON<{ runs: WorkflowRunResponse[] }>(
    `/api/workflows/runs${qs ? `?${qs}` : ''}`
  );
  return result.runs;
}

export async function getWorkflowRun(
  runId: string
): Promise<{ run: WorkflowRunResponse; events: WorkflowEventResponse[] }> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}`);
}

export async function listWorkflowArtifacts(runId: string): Promise<WorkflowArtifactListResponse> {
  return fetchJSON(`/api/workflows/runs/${encodeURIComponent(runId)}/artifacts`);
}

export async function getWorkflowRunByWorker(
  workerPlatformId: string
): Promise<{ run: WorkflowRunResponse } | null> {
  try {
    return await fetchJSON(`/api/workflows/runs/by-worker/${encodeURIComponent(workerPlatformId)}`);
  } catch (e: unknown) {
    // 404 means no run exists yet — expected during dispatch
    if ((e as Error & { status?: number }).status === 404) {
      return null;
    }
    throw e;
  }
}

type WorkflowSource = components['schemas']['WorkflowSource'];

export interface GetWorkflowResponse {
  workflow: WorkflowDefinition;
  filename: string;
  source: WorkflowSource;
}

export async function getWorkflow(name: string, cwd?: string): Promise<GetWorkflowResponse> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/workflows/${encodeURIComponent(name)}${params}`);
}

export async function saveWorkflow(
  name: string,
  definition: WorkflowDefinition,
  cwd?: string
): Promise<GetWorkflowResponse> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/workflows/${encodeURIComponent(name)}${params}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ definition }),
  });
}

export async function deleteWorkflow(
  name: string,
  cwd?: string
): Promise<{ deleted: boolean; name: string }> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/workflows/${encodeURIComponent(name)}${params}`, {
    method: 'DELETE',
  });
}

export async function validateWorkflow(
  definition: WorkflowDefinition
): Promise<{ valid: boolean; errors?: string[] }> {
  return fetchJSON('/api/workflows/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ definition }),
  });
}

export interface CommandSummary {
  name: string;
  source: WorkflowSource;
}

export type CommandEntry = CommandSummary;

export interface CommandResponse {
  name: string;
  body: string;
  source: WorkflowSource;
  version: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CommandVersion {
  version: number;
  body: string;
  savedAt: string;
}

export async function listCommands(cwd?: string): Promise<CommandSummary[]> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  const result = await fetchJSON<{ commands: CommandSummary[] }>(`/api/commands${params}`);
  return result.commands;
}

export async function getCommand(name: string, cwd?: string): Promise<CommandResponse> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/commands/${encodeURIComponent(name)}${params}`);
}

export async function saveCommand(
  name: string,
  body: string,
  cwd?: string
): Promise<CommandResponse> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/commands/${encodeURIComponent(name)}${params}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
}

export async function deleteCommand(
  name: string,
  cwd?: string
): Promise<{ deleted: boolean; name: string }> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/commands/${encodeURIComponent(name)}${params}`, {
    method: 'DELETE',
  });
}

export async function getCommandVersions(
  name: string,
  cwd?: string
): Promise<{ versions: CommandVersion[] }> {
  const params = cwd ? `?cwd=${encodeURIComponent(cwd)}` : '';
  return fetchJSON(`/api/commands/${encodeURIComponent(name)}/versions${params}`);
}

export async function getConfig(): Promise<{ config: SafeConfigResponse; database: string }> {
  return fetchJSON('/api/config');
}

export async function updateAssistantConfig(
  body: UpdateAssistantConfigBody
): Promise<{ config: SafeConfigResponse; database: string }> {
  return fetchJSON('/api/config/assistants', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export type IsolationEnvironment = components['schemas']['IsolationEnvironment'];

export async function getCodebaseEnvironments(codebaseId: string): Promise<IsolationEnvironment[]> {
  const result = await fetchJSON<{ environments: IsolationEnvironment[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/environments`
  );
  return result.environments;
}

// Codebase env vars
export async function getCodebaseEnvVars(codebaseId: string): Promise<string[]> {
  const result = await fetchJSON<{ keys: string[] }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/env`
  );
  return result.keys;
}

export async function setCodebaseEnvVar(
  codebaseId: string,
  data: { key: string; value: string }
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(`/api/codebases/${encodeURIComponent(codebaseId)}/env`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteCodebaseEnvVar(
  codebaseId: string,
  key: string
): Promise<{ success: boolean }> {
  return fetchJSON<{ success: boolean }>(
    `/api/codebases/${encodeURIComponent(codebaseId)}/env/${encodeURIComponent(key)}`,
    { method: 'DELETE' }
  );
}

// System
export async function getHealth(): Promise<HealthResponse> {
  return fetchJSON<HealthResponse>('/api/health');
}

export async function getReadiness(): Promise<ReadinessResponse> {
  // Always parse the body — both 200 (ready) and 503 (not_ready) carry the report.
  const res = await fetch(resolveApiUrl('/api/health/ready'), { credentials: 'include' });
  if (res.status !== 200 && res.status !== 503) {
    throw new Error(`Readiness endpoint returned HTTP ${String(res.status)}`);
  }
  return (await res.json()) as ReadinessResponse;
}

export async function getKillSwitchState(): Promise<KillSwitchState> {
  return fetchJSON<KillSwitchState>('/api/system/kill-switches');
}

export async function getPublicationReconcileDiff(
  artifactId: string,
  workItemId: string
): Promise<ReconcileDiff> {
  const query = new URLSearchParams({ artifactId, workItemId });
  return fetchJSON<ReconcileDiff>(`/api/admin/artifact-publications/reconcile?${query}`);
}

export async function applyPublicationReconcileDecision(
  decision: PublicationReconcileDecision
): Promise<PublicationReconcileDecisionResponse> {
  return fetchJSON<PublicationReconcileDecisionResponse>(
    '/api/admin/artifact-publications/reconcile',
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(decision),
    }
  );
}

// =========================================================================
// Phase 6: Scripts, Skills, MCP Configs
// =========================================================================

export type ScriptRuntime = 'bun' | 'uv';
type ResourceSource = 'bundled' | 'platform' | 'project';

export interface ScriptSummary {
  name: string;
  runtime: ScriptRuntime;
  source: ResourceSource;
  version: number | null;
  updatedAt: string | null;
}

export interface ScriptResponse {
  id: string;
  name: string;
  runtime: ScriptRuntime;
  body: string;
  deps: unknown;
  timeoutMs: number | null;
  ownerId: string | null;
  version: number;
  source: ResourceSource;
  createdAt: string;
  updatedAt: string;
}

export interface SaveScriptInput {
  runtime: ScriptRuntime;
  body: string;
  deps?: unknown;
  timeoutMs?: number;
}

export async function listScripts(): Promise<{ scripts: ScriptSummary[] }> {
  return fetchJSON('/api/scripts');
}
export async function getScript(name: string): Promise<ScriptResponse> {
  return fetchJSON(`/api/scripts/${encodeURIComponent(name)}`);
}
export async function saveScript(name: string, data: SaveScriptInput): Promise<ScriptResponse> {
  return fetchJSON(`/api/scripts/${encodeURIComponent(name)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}
export async function deleteScript(name: string): Promise<{ success: boolean }> {
  return fetchJSON(`/api/scripts/${encodeURIComponent(name)}`, { method: 'DELETE' });
}
export async function getScriptVersions(
  name: string
): Promise<{ versions: { version: number; savedAt: string; body: string }[] }> {
  return fetchJSON(`/api/scripts/${encodeURIComponent(name)}/versions`);
}

export interface SkillSummary {
  name: string;
  description: string | null;
  totalSizeBytes: number;
  source: 'bundled' | 'platform';
  version: number;
  updatedAt: string;
}
interface SkillFile {
  path: string;
  content: string;
}
export interface SkillResponse {
  id: string;
  name: string;
  description: string | null;
  ownerId: string | null;
  version: number;
  totalSizeBytes: number;
  source: 'bundled' | 'platform';
  createdAt: string;
  updatedAt: string;
  files: SkillFile[];
}
export interface SaveSkillInput {
  description?: string | null;
  files: Record<string, string>;
}

export async function listSkills(): Promise<{ skills: SkillSummary[] }> {
  return fetchJSON('/api/skills');
}
export async function getSkill(name: string): Promise<SkillResponse> {
  return fetchJSON(`/api/skills/${encodeURIComponent(name)}`);
}
export async function saveSkill(name: string, data: SaveSkillInput): Promise<SkillResponse> {
  return fetchJSON(`/api/skills/${encodeURIComponent(name)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}
export async function deleteSkill(name: string): Promise<{ success: boolean }> {
  return fetchJSON(`/api/skills/${encodeURIComponent(name)}`, { method: 'DELETE' });
}
export async function saveSkillFile(
  name: string,
  path: string,
  content: string
): Promise<{ success: boolean }> {
  return fetchJSON(`/api/skills/${encodeURIComponent(name)}/files/${encodeURIComponent(path)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });
}
export async function deleteSkillFile(name: string, path: string): Promise<{ success: boolean }> {
  return fetchJSON(`/api/skills/${encodeURIComponent(name)}/files/${encodeURIComponent(path)}`, {
    method: 'DELETE',
  });
}

export interface McpConfigSummary {
  name: string;
  description: string | null;
  serverNames: string[];
  source: 'bundled' | 'platform';
  version: number;
  updatedAt: string;
}
export interface McpConfigResponse {
  id: string;
  name: string;
  description: string | null;
  configJson: Record<string, unknown>;
  ownerId: string | null;
  version: number;
  source: 'bundled' | 'platform';
  createdAt: string;
  updatedAt: string;
}
export interface SaveMcpConfigInput {
  description?: string | null;
  configJson: Record<string, unknown>;
}

export async function listMcpConfigs(): Promise<{ mcpConfigs: McpConfigSummary[] }> {
  return fetchJSON('/api/mcp-configs');
}
export async function getMcpConfig(name: string): Promise<McpConfigResponse> {
  return fetchJSON(`/api/mcp-configs/${encodeURIComponent(name)}`);
}
export async function saveMcpConfig(
  name: string,
  data: SaveMcpConfigInput
): Promise<McpConfigResponse> {
  return fetchJSON(`/api/mcp-configs/${encodeURIComponent(name)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}
export async function deleteMcpConfig(name: string): Promise<{ success: boolean }> {
  return fetchJSON(`/api/mcp-configs/${encodeURIComponent(name)}`, { method: 'DELETE' });
}
